import { Injectable, inject } from '@angular/core';
import { GeoJSONSource, LngLatBounds, Map, Marker } from 'maplibre-gl';
import { UtilsService } from '../utils/utils.service';
import { Route, RoutePlanElement, RoutePlanType, RoadSurfaceAlert, Incident, RouteEVSpeedLimitSegment } from 'src/app/models/route';
import { MapService } from './map.service';
import { MapDrawerHelperService } from './map-drawer-helper.service';
import { MapChargingStationDrawerService } from './map-charging-station-drawer.service';
import { chargerPowerClass } from 'src/app/models/charging-station';
import { MapChargingPopupService, PopupAlign } from './map-charging-popup.service';
import { SettingsService, MapSkin, Unit } from '../account/settings.service';
import { LanguageService } from '../account/language.service';
import { LatLng } from 'src/app/models/latlng';
import { Waypoint } from 'src/app/models/range-params';
import { TripPoint } from 'src/app/models/trip';
import { scaleRaster } from './map-symbol-scale';
import { CursorService } from './cursor.service';

// Color constants
const ROUTE_LINE_TOP_COLOR = '#000000';
const ROUTE_LINE_MIDDLE_COLOR = '#469f9b';
const ROUTE_LINE_BASE_COLOR = '#ffffff';
const ALT_ROUTE_LINE_TOP_COLOR = '#000000';
const ALT_ROUTE_LINE_MIDDLE_COLOR = '#ffffff';
const ALT_ROUTE_LINE_BASE_COLOR = '#000000';
// Already-driven part of the navigation route. Same in both skins on purpose, and
// shared with iOS (MapPalette.routeTraveled) and Android Auto – change all three together.
const TRAVELED_ROUTE_COLOR = '#A0A3A8';
// The traveled line is two sources: a long fixed part, rebuilt rarely, and a short tip
// up to the cursor that is updated every frame. The tip is folded into the fixed part
// once it spans this much route.
const TRAVELED_FOLD_STEP_METERS = 200;
// The boundary only follows a cursor this close to the route (raw GPS noise included).
const TRAVELED_MAX_CURSOR_OFFSET_METERS = 50;
const METERS_PER_DEGREE_LAT = 111320;

// Incident and road surface alert icons are skin independent, one set serves both map skins.
const INCIDENT_ICON_BASE = 'assets/icons/default/map/incidents';
const ROAD_SURFACE_ALERT_ICON_BASE = 'assets/icons/default/map/road_surface_alerts';

@Injectable({
  providedIn: 'root',
})
export class MapRouteDrawerService {

  private mapInstance: Map | null = null;
  private routeMarkers: Marker[] = [];
  private evSpeedLimitMarkers: Marker[] = [];
  private failedRouteMarkers: Marker[] = [];
  private tripMarkers: Marker[] = [];
  private waypointMarkerElements: HTMLElement[] = [];
  private inactiveWaypointCount = 0;

  // Traveled-route state. The boundary is the cursor projected onto route segment
  // traveledSeg (points[traveledSeg] -> points[traveledSeg + 1]) at traveledFrac.
  // The distances only decide when to fold the tip, never where the boundary is.
  private traveledRoute: Route | null = null;
  private cumulativeDistances: number[] = [];
  private traveledActive = false;
  private traveledMaxSeg = -1;     // last segment the engine allows: the one ending at its target point
  private traveledSeg = 0;
  private traveledFrac = 0;
  private traveledFixedEnd = 0;    // points 0..traveledFixedEnd are in the fixed source

  private readonly utilsService = inject(UtilsService);
  private readonly mapService = inject(MapService);
  private readonly mapDrawerHelperService = inject(MapDrawerHelperService);
  private readonly mapChargingStationDrawerService = inject(MapChargingStationDrawerService);
  private readonly mapChargingPopupService = inject(MapChargingPopupService);
  private readonly settingsService = inject(SettingsService);
  private readonly languageService = inject(LanguageService);
  private readonly cursorService = inject(CursorService);

  constructor() {
    // Follow the cursor as drawn, frame by frame, not the latest GPS fix: the cursor
    // glides towards each fix, so the fix is ahead of what the driver sees.
    this.cursorService.ObservableCursorMoved.subscribe(({ lon, lat }) => this.advanceTraveledTo(lon, lat));
  }

  public setMapInstance(map: Map): void {
    this.mapInstance = map;
  }

  private getFirstLabelLayerId(): string | undefined {
    if (!this.mapInstance) return undefined;
    const layers = this.mapInstance.getStyle()?.layers;
    if (!layers) return undefined;
    return layers.find(l => l.type === 'symbol' && ('text-field' in (l.layout || {})))?.id;
  }

  public drawRoutePlan(resp: any, mainRoute: number) {
    this.clearRoute();
    if (resp && resp.length > 0) {
      // Draw alternative routes first - filter out mainRoute
      resp.filter((el: any) => el.routeplanmode !== mainRoute).forEach((el: any) => {
        if (resp[el.routeplanmode]) {
          this.drawAlternativeRoute(resp[el.routeplanmode].points, el.routeplanmode);
        }
      });

      // Draw main route last so it appears on top
      this.drawMainRoute(resp.find((el: any) => el.routeplanmode === mainRoute)?.points || []);
    }
  }

  public drawMainRoute(route: any): void {
    // Add battery icons if not already added
    if (!this.mapInstance?.hasImage('routing_battery_50')) {
      const battery50Icon = new Image();
      battery50Icon.src = 'icons/routing_battery_50.svg';
      battery50Icon.width = scaleRaster(96);
      battery50Icon.height = scaleRaster(120);
      battery50Icon.onload = () => {
        this.mapInstance?.addImage('routing_battery_50', battery50Icon);
      }
    }
    if (!this.mapInstance?.hasImage('routing_battery_empty')) {
      const batteryEmptyIcon = new Image();
      batteryEmptyIcon.src = 'icons/routing_battery_empty.svg';
      batteryEmptyIcon.width = scaleRaster(96);
      batteryEmptyIcon.height = scaleRaster(120);
      batteryEmptyIcon.onload = () => {
        this.mapInstance?.addImage('routing_battery_empty', batteryEmptyIcon);
      }
    }
    if (!this.mapInstance?.hasImage('arrow')) {
      const arrow = new Image();
      arrow.src = 'icons/arrow.svg';
      arrow.width = 22;
      arrow.height = 25;
      arrow.onload = () => {
        this.mapInstance?.addImage('arrow', arrow);
      }
    }

    // Find the first point where SOC drops to 50% or below
    const lowBatteryPoint = route.find(point => point.soc !== undefined && point.soc <= 50);

    // Find the first point where SOC reaches 0
    const emptyBatteryPoint = route.find(point => point.soc !== undefined && point.soc <= 0);

    // Convert route to GeoJSON LineString
    const routeCoordinates = route.map(point => [point.lon, point.lat]);
    const labelLayerId = this.getFirstLabelLayerId();

    // Add new source for the route
    this.mapInstance?.addSource('route', {
      'type': 'geojson',
      'data': {
        'type': 'Feature',
        'properties': {},
        'geometry': {
          'type': 'LineString',
          'coordinates': routeCoordinates
        }
      }
    });

    // Add black center line (thinnest)
    this.mapInstance?.addLayer({
      'id': 'route-line-top',
      'type': 'line',
      'source': 'route',
      'layout': {
        'line-join': 'round',
        'line-cap': 'round'
      },
      'paint': {
        'line-color': ROUTE_LINE_TOP_COLOR,
        'line-width': 16
      }
    }, labelLayerId);

    // Add middle teal layer
    this.mapInstance?.addLayer({
      'id': 'route-line-middle',
      'type': 'line',
      'source': 'route',
      'layout': {
        'line-join': 'round',
        'line-cap': 'round'
      },
      'paint': {
        'line-color': ROUTE_LINE_MIDDLE_COLOR,
        'line-width': 15
      }
    }, labelLayerId);

    // Add white base layer (widest)
    this.mapInstance?.addLayer({
      'id': 'route-line-base',
      'type': 'line',
      'source': 'route',
      'layout': {
        'line-join': 'round',
        'line-cap': 'round'
      },
      'paint': {
        'line-color': ROUTE_LINE_BASE_COLOR,
        'line-width': 10
      }
    }, labelLayerId);

    // Add arrow symbols along the line
    this.mapInstance?.addLayer({
      'id': 'route-arrows',
      'type': 'symbol',
      'source': 'route',
      'layout': {
        'symbol-placement': 'line',
        'symbol-spacing': 75,
        'icon-image': 'arrow',
        'icon-size': 0.35,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-rotation-alignment': 'map',
        'icon-pitch-alignment': 'map'
      }
    });

    // Add routing_target icon if not already added
    if (this.mapInstance?.hasImage('routing_target')) {
      this.addRouteTargetMarker(route);
    } else {
      const targetImg = new Image();
      targetImg.width = scaleRaster(96);
      targetImg.height = scaleRaster(120);
      targetImg.src = 'icons/routing_target.svg';
      targetImg.onload = () => {
        this.mapInstance?.addImage('routing_target', targetImg);
        this.addRouteTargetMarker(route);
      };
    }

    // Add battery 50% marker if needed
    if (lowBatteryPoint) {
      this.mapInstance?.addSource('battery-warning', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lowBatteryPoint.lon, lowBatteryPoint.lat]
          },
          properties: {}
        }
      });

      this.mapInstance?.addLayer({
        id: 'battery-warning-marker',
        type: 'symbol',
        source: 'battery-warning',
        layout: {
          'icon-image': 'routing_battery_50',
          'icon-size': 0.45,
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true
        }
      });
    }

    // Add battery empty marker if needed
    if (emptyBatteryPoint) {
      this.mapInstance?.addSource('battery-empty', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [emptyBatteryPoint.lon, emptyBatteryPoint.lat]
          },
          properties: {}
        }
      });

      this.mapInstance?.addLayer({
        id: 'battery-empty-marker',
        type: 'symbol',
        source: 'battery-empty',
        layout: {
          'icon-image': 'routing_battery_empty',
          'icon-size': 0.45,
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true
        }
      });
    }

    this.keepChargingStationLayersOnTop();
  }

  public drawAlternativeRoute(route: { lat: number, lon: number }[], index: number): void {
    const routeId = `route-alt-${index}`;
    const labelLayerId = this.getFirstLabelLayerId();

    // Add arrow symbol image if not already added
    if (!this.mapInstance?.hasImage('arrow_white')) {
      const arrow = new Image();
      arrow.src = 'icons/arrow_white.svg';
      arrow.width = 22;
      arrow.height = 25;
      arrow.onload = () => {
        this.mapInstance?.addImage('arrow_white', arrow);
      }
    }

    // Convert route to GeoJSON LineString
    const routeCoordinates = route.map(point => [point.lon, point.lat]);

    // Add new source for the route
    this.mapInstance?.addSource(routeId, {
      'type': 'geojson',
      'data': {
        'type': 'Feature',
        'properties': {},
        'geometry': {
          'type': 'LineString',
          'coordinates': routeCoordinates
        }
      }
    });

    // Add black center line (thinnest)
    this.mapInstance?.addLayer({
      'id': `${routeId}-line-top`,
      'type': 'line',
      'source': routeId,
      'layout': {
        'line-join': 'round',
        'line-cap': 'round'
      },
      'paint': {
        'line-color': ALT_ROUTE_LINE_TOP_COLOR,
        'line-width': 16
      }
    }, labelLayerId);

    // Add middle colored layer
    this.mapInstance?.addLayer({
      'id': `${routeId}-line-middle`,
      'type': 'line',
      'source': routeId,
      'layout': {
        'line-join': 'round',
        'line-cap': 'round'
      },
      'paint': {
        'line-color': ALT_ROUTE_LINE_MIDDLE_COLOR,
        'line-width': 15
      }
    }, labelLayerId);

    // Add white base layer (widest)
    this.mapInstance?.addLayer({
      'id': `${routeId}-line-base`,
      'type': 'line',
      'source': routeId,
      'layout': {
        'line-join': 'round',
        'line-cap': 'round'
      },
      'paint': {
        'line-color': ALT_ROUTE_LINE_BASE_COLOR,
        'line-width': 10
      }
    }, labelLayerId);

    // Add arrow symbols along the line
    this.mapInstance?.addLayer({
      'id': `${routeId}-arrows`,
      'type': 'symbol',
      'source': routeId,
      'layout': {
        'symbol-placement': 'line',
        'symbol-spacing': 75,
        'icon-image': 'arrow_white',
        'icon-size': 0.35,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-rotation-alignment': 'map',
        'icon-pitch-alignment': 'map'
      }
    });

    this.keepChargingStationLayersOnTop();
  }

  // Helper to add the symbol layer for the target marker
  private addRouteTargetMarker(route: any) {
    if (!route.length) return;

    // Remove previous target marker if exists
    if (this.mapInstance?.getLayer('route-target-marker')) {
      this.mapInstance.removeLayer('route-target-marker');
    }
    if (this.mapInstance?.getSource('route-target-marker')) {
      this.mapInstance.removeSource('route-target-marker');
    }

    // Add GeoJSON source for the target marker (last point)
    const last = route[route.length - 1];
    this.mapInstance?.addSource('route-target-marker', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [last.lon, last.lat]
          },
          properties: {}
        }]
      }
    });

    // Add symbol layer for the target marker
    this.mapInstance?.addLayer({
      id: 'route-target-marker',
      type: 'symbol',
      source: 'route-target-marker',
      layout: {
        'icon-image': 'routing_target',
        'icon-size': 0.5,
        'icon-anchor': 'bottom'
      }
    });
  }

  // Draw a fully planned route to the map
  public async drawRouteToMap(routeData: Route): Promise<void> {
    if (!this.mapInstance) return;

    this.clearRoute();
    this.clearRouteMarkers();

    const skin = this.settingsService.getMap().Skin;
    const routePlan = routeData.RoutePlan;

    const endPoint = this.mapService.getRoutePlanEndPoint(routePlan);

    // 1. Route polyline (canvas layers – always below all DOM markers)
    this.drawRoutePolyline(routeData, skin);

    // 2. Road Surface Alerts & Incidents (symbol layers on canvas, above route, below CS icons)
    await this.drawRoadSurfaceAlerts(routeData.RoadSurfaceAlerts);
    await this.drawIncidents(routeData.Incidents);

    // 3. EV Speed Limit segments
    this.drawEVSpeedLimits(routeData, skin);

    // 4. End marker as HTML Marker (above alerts). A charger destination gets one
    // combined charger + finish marker instead, drawn with the other chargers below.
    if (endPoint && endPoint.Type !== RoutePlanType.ChargerEndPoint) {
      const endIconUrl = `assets/icons/${skin}/map/route_point_target.svg?v=2`;
      const endEl = document.createElement('div');
      endEl.style.lineHeight = '0';
      endEl.innerHTML = `<img src='${endIconUrl}' width='${scaleRaster(48)}' height='${scaleRaster(48)}'/>`;
      const endMarker = new Marker({ element: endEl, anchor: 'bottom' })
        .setLngLat([endPoint.Location.lng, endPoint.Location.lat])
        .addTo(this.mapInstance);
      this.routeMarkers.push(endMarker);

      // End marker remaining battery label
      const endBatteryEl = document.createElement('div');
      endBatteryEl.className = 'charge-time';
      endBatteryEl.innerHTML = `<div class='charge-time-inner'>
        <svg class='re-bat-icon' viewBox='0 0 28 14' fill='none' xmlns='http://www.w3.org/2000/svg'
          style='width:${scaleRaster(20)}px;height:${scaleRaster(10)}px;flex-shrink:0;color:var(--base-white-color);margin-right:4px;'>
          <rect x='0.75' y='0.75' width='24.5' height='12.5' rx='2.25' stroke='currentColor' stroke-width='1.5'/>
          <path d='M26.5 4.5v5' stroke='currentColor' stroke-width='1.5' stroke-linecap='round'/>
          <rect class='re-bat-fill' x='2' y='2' height='10' width='${endPoint.BatteryLevel * 0.22}' rx='1.25'
            style='fill:var(--base-white-color)'/>
        </svg>
        <h4>${endPoint.BatteryLevel}%</h4></div>`;
      const endBatteryMarker = new Marker({ element: endBatteryEl, anchor: 'bottom', offset: [0, scaleRaster(-56)] })
        .setLngLat([endPoint.Location.lng, endPoint.Location.lat])
        .addTo(this.mapInstance);
      this.routeMarkers.push(endBatteryMarker);

      // End marker battery reserved warning
      if (endPoint.BatteryReservedWarning) {
        this.addBatteryReservedWarningMarker(endPoint.Location);
      }
    }

    // 5. Waypoint markers (above alerts)
    this.mapService.getRoutePlanByType(routePlan, RoutePlanType.WayPoint).forEach((wp: RoutePlanElement) => {
      const el = document.createElement('div');
      el.className = 'waypoint-icon-outer';
      el.innerHTML = `<div class='waypoint-icon'>
        <img src='assets/icons/${skin}/map/route_point_waypoint.svg?v=3'></div>
        <div class='waypoint-idx'>${wp.WaypointIndex}</div>`;
      el.style.cursor = 'pointer';
      el.addEventListener('click', () => {
        this.mapService.ObservableActivePlanningPanelRouteElement.next({ type: 'waypoint', index: wp.WaypointIndex! - 1 });
      });

      const marker = new Marker({ element: el, anchor: 'bottom' })
        .setLngLat([wp.Location.lng, wp.Location.lat])
        .addTo(this.mapInstance!);
      this.routeMarkers.push(marker);
      this.waypointMarkerElements.push(el);

      if (wp.BatteryReservedWarning) {
        this.addBatteryReservedWarningMarker(wp.Location);
      }
    });

    // Keep already-passed waypoints gray across redraws (e.g. skin change mid-navigation)
    this.applyInactiveWaypoints();

    // 6. Charging stations along the route (topmost DOM markers)
    this.drawRouteChargingStations(routeData, skin);
    if (endPoint?.Type === RoutePlanType.ChargerEndPoint) {
      this.drawChargerEndPoint(endPoint, skin);
    }

    // 7. Filter out route charging stations from the tile-based CS layer
    const routeChargerIds = [
      ...this.mapService.getRoutePlanByType(routePlan, RoutePlanType.Charger),
      ...this.mapService.getRoutePlanByType(routePlan, RoutePlanType.ChargerEndPoint)
    ]
      .map((c: RoutePlanElement) => c.ChargingStationId)
      .filter((id): id is string => id !== null);
    this.mapChargingStationDrawerService.setRouteChargingStationIds(routeChargerIds);

    this.keepChargingStationLayersOnTop();

    // Fit map to show the entire route
    this.fitMapToRoute(routeData);
  }

  private fitMapToRoute(routeData: Route): void {
    if (!this.mapInstance || routeData.RoutePoints.length === 0) return;

    const bounds = new LngLatBounds();
    routeData.RoutePoints.forEach(p => bounds.extend([p.Location.lng, p.Location.lat]));

    if (!bounds.isEmpty()) {
      this.mapInstance.fitBounds(bounds, { padding: this.mapDrawerHelperService.getRoutePadding(), animate: true });
      // Once the camera settles, promote the charge-time markers to their own
      // compositor layer so their text rasterizes crisply at the final
      // position. Doing this earlier (or via base CSS) caches a blurry raster
      // taken while fitBounds is still animating.
      this.mapInstance.once('moveend', () => this.sharpenRouteMarkers());
    }
  }

  /**
   * Adds the `gpu-crisp` class to every charge-time marker so the browser
   * rasterizes their text on a dedicated compositor layer, aligned to the
   * device-pixel grid. Called after the camera stops moving; on fractional
   * devicePixelRatio (Windows display scaling) this removes the sub-pixel
   * blur that otherwise appears once the map fits to the route.
   */
  private sharpenRouteMarkers(): void {
    this.routeMarkers.forEach(marker => {
      marker.getElement()
        .querySelectorAll<HTMLElement>('.charge-time-inner')
        .forEach(inner => inner.classList.add('gpu-crisp'));
    });
  }

  /**
   * Builds one LineString feature per run of consecutive segments that share the
   * same colour. `segmentColors[i]` is the colour of the segment between
   * `coordinates[i]` and `coordinates[i + 1]`, so it holds one entry less than
   * `coordinates`. Neighbouring runs share their boundary coordinate, so the
   * merged features join seamlessly.
   *
   * Emitting a separate feature per point pair instead produces gaps at wide
   * zoom levels: MapLibre tiles GeoJSON sources with geojson-vt, which drops any
   * line feature shorter than the tile simplification tolerance
   * (0.375 / (2^z * 4096) in Mercator units – roughly 75 m at zoom 5). Route
   * points are usually far denser than that, so entire stretches of pair
   * features disappeared at once. A merged run spans many points and stays well
   * above the tolerance; if one is still dropped it is by definition shorter
   * than ~0.05 CSS px and therefore invisible.
   */
  private buildColorRunFeatures(coordinates: [number, number][], segmentColors: string[]): GeoJSON.Feature[] {
    const features: GeoJSON.Feature[] = [];
    if (coordinates.length < 2) return features;

    let runStart = 0;
    for (let i = 1; i <= segmentColors.length; i++) {
      // Keep extending the current run while the colour is unchanged
      if (i < segmentColors.length && segmentColors[i] === segmentColors[runStart]) continue;

      features.push({
        type: 'Feature',
        properties: { color: segmentColors[runStart] },
        geometry: { type: 'LineString', coordinates: coordinates.slice(runStart, i + 1) }
      });
      runStart = i;
    }

    return features;
  }

  /**
   * Let the traveled line advance up to the engine's current segment. `targetIdx`
   * is NavigationService's target route point, still ahead of the driver, so the
   * driver is on the segment that ends at it. Invalid/replanning targets (<= 0)
   * are ignored. Where on those segments the boundary actually sits is decided by
   * the cursor (advanceTraveledTo), so it never leads what the driver sees.
   */
  public updateTraveledProgress(targetIdx: number): void {
    const route = this.traveledRoute;
    if (!route || targetIdx <= 0) return;

    if (!this.traveledActive) {
      this.traveledActive = true;
      // Puts the start-cap disc in place; the fixed line itself is still empty
      this.setSourceData('planned-route-traveled', this.buildTraveledFixed());
    }
    const maxSeg = Math.min(targetIdx, route.RoutePoints.length - 1) - 1;
    this.traveledMaxSeg = Math.max(this.traveledMaxSeg, maxSeg);
  }

  /**
   * Move the grey/colour boundary to the cursor, projected onto the route between
   * the current boundary and the engine's segment. Only ever moves forward.
   */
  private advanceTraveledTo(lon: number, lat: number): void {
    const route = this.traveledRoute;
    if (!route || !this.traveledActive) return;

    // Planar projection with longitude scaled by cos(lat) – exact enough over one segment
    const points = route.RoutePoints;
    const cosLat = Math.cos(lat * Math.PI / 180);
    const px = lon * cosLat;
    let bestSeg = -1;
    let bestFrac = 0;
    let bestDist = Infinity;
    for (let k = this.traveledSeg; k <= this.traveledMaxSeg; k++) {
      const ax = points[k].Location.lng * cosLat, ay = points[k].Location.lat;
      const dx = points[k + 1].Location.lng * cosLat - ax, dy = points[k + 1].Location.lat - ay;
      const len2 = dx * dx + dy * dy;
      const t = len2 > 0 ? Math.min(1, Math.max(0, ((px - ax) * dx + (lat - ay) * dy) / len2)) : 0;
      const ex = ax + t * dx - px, ey = ay + t * dy - lat;
      const dist = ex * ex + ey * ey;
      if (dist < bestDist) {
        bestSeg = k;
        bestFrac = t;
        bestDist = dist;
      }
    }

    // A cursor off the route – e.g. still gliding in from where it was before the
    // drive started – would project onto a segment end and grey it out for good.
    if (Math.sqrt(bestDist) * METERS_PER_DEGREE_LAT > TRAVELED_MAX_CURSOR_OFFSET_METERS) return;
    if (bestSeg < this.traveledSeg || (bestSeg === this.traveledSeg && bestFrac <= this.traveledFrac)) return;
    this.traveledSeg = bestSeg;
    this.traveledFrac = bestFrac;

    // Fold the tip into the fixed line now and then, so the per-frame update stays small
    if (this.cumulativeDistances[this.traveledSeg] - this.cumulativeDistances[this.traveledFixedEnd] >= TRAVELED_FOLD_STEP_METERS) {
      this.traveledFixedEnd = this.traveledSeg;
      this.setSourceData('planned-route-traveled', this.buildTraveledFixed());
    }
    this.setSourceData('planned-route-traveled-tip', this.buildTraveledTip());
  }

  /** Navigation ended: nothing is behind the driver any more. */
  public resetTraveledProgress(): void {
    this.resetTraveledState();
    this.setSourceData('planned-route-traveled', this.buildTraveledFixed());
    this.setSourceData('planned-route-traveled-tip', this.buildTraveledTip());
  }

  private resetTraveledState(): void {
    this.traveledActive = false;
    this.traveledMaxSeg = -1;
    this.traveledSeg = 0;
    this.traveledFrac = 0;
    this.traveledFixedEnd = 0;
  }

  /**
   * Route points 0..traveledFixedEnd, plus a point at the route start: the coloured
   * line's round start cap pokes out behind the first route point, and the butt-capped
   * tip cannot cover it before the fixed line exists.
   */
  private buildTraveledFixed(): GeoJSON.FeatureCollection {
    const points = this.traveledRoute?.RoutePoints ?? [];
    if (!this.traveledActive || points.length === 0) return this.lineFeatureCollection([]);

    const coordinates = points.slice(0, this.traveledFixedEnd + 1).map(p => [p.Location.lng, p.Location.lat]);
    const collection = this.lineFeatureCollection(coordinates);
    collection.features.push({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: coordinates[0] } });
    return collection;
  }

  /** From the end of the fixed line through the passed route points to the boundary point. */
  private buildTraveledTip(): GeoJSON.FeatureCollection {
    const points = this.traveledRoute?.RoutePoints ?? [];
    if (!this.traveledActive || this.traveledSeg + 1 >= points.length) return this.lineFeatureCollection([]);

    const coordinates = points.slice(this.traveledFixedEnd, this.traveledSeg + 1).map(p => [p.Location.lng, p.Location.lat]);
    const a = points[this.traveledSeg].Location, b = points[this.traveledSeg + 1].Location;
    coordinates.push([a.lng + (b.lng - a.lng) * this.traveledFrac, a.lat + (b.lat - a.lat) * this.traveledFrac]);
    return this.lineFeatureCollection(coordinates);
  }

  private lineFeatureCollection(coordinates: number[][]): GeoJSON.FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: coordinates.length < 2 ? [] : [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates } }]
    };
  }

  private setSourceData(sourceId: string, data: GeoJSON.FeatureCollection): void {
    this.mapInstance?.getSource<GeoJSONSource>(sourceId)?.setData(data);
  }

  private drawRoutePolyline(routeData: Route, skin: MapSkin): void {
    if (!this.mapInstance || routeData.RoutePoints.length < 2) return;

    const labelLayerId = this.getFirstLabelLayerId();
    const coordinates: [number, number][] = routeData.RoutePoints.map(p => [p.Location.lng, p.Location.lat]);

    // A new route (first plan or a replan landing) starts with nothing behind the
    // driver. Redrawing the same route (e.g. a skin change) keeps the progress.
    if (routeData !== this.traveledRoute) {
      this.traveledRoute = routeData;
      this.resetTraveledState();
      let sum = 0;
      this.cumulativeDistances = routeData.RoutePoints.map((p, i) => sum += i > 0 ? p.DistanceFromPreviousPoint : 0);
    }

    // Build colour runs for the jam-factor coloured line
    const segmentColors = routeData.RoutePoints.slice(0, -1)
      .map(p => this.utilsService.getColorStringForJamFactor(p.JamFactor, skin));
    const segmentFeatures = this.buildColorRunFeatures(coordinates, segmentColors);

    // Full route source (for outline and white line)
    this.mapInstance.addSource('planned-route', {
      type: 'geojson',
      data: { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates } }
    });

    // Segments source (for coloured jam-factor line)
    this.mapInstance.addSource('planned-route-segments', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: segmentFeatures }
    });

    // Grey outline – light mode only
    if (skin === MapSkin.Light) {
      this.mapInstance.addLayer({
        id: 'planned-route-outline',
        type: 'line',
        source: 'planned-route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#ccc', 'line-width': 18 }
      }, labelLayerId);
    }

    // White line
    this.mapInstance.addLayer({
      id: 'planned-route-white',
      type: 'line',
      source: 'planned-route',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color':  skin === MapSkin.Light ? '#ffffff' : '#000000', 'line-width': 16 }
    }, labelLayerId);

    // Coloured jam-factor line (data-driven colour per segment)
    this.mapInstance.addLayer({
      id: 'planned-route-colored',
      type: 'line',
      source: 'planned-route-segments',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 9
      }
    }, labelLayerId);

    // Traveled part: solid grey painted over the coloured line at the same width; its
    // geometry ends exactly at the driver, which makes the boundary a hard edge.
    this.mapInstance.addSource('planned-route-traveled', { type: 'geojson', data: this.buildTraveledFixed() });
    this.mapInstance.addSource('planned-route-traveled-tip', { type: 'geojson', data: this.buildTraveledTip() });
    // Grey disc matching the line's round cap (radius = half the width), lying on the map plane like the line
    this.mapInstance.addLayer({
      id: 'planned-route-traveled-start',
      type: 'circle',
      source: 'planned-route-traveled',
      filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-color': TRAVELED_ROUTE_COLOR, 'circle-radius': 4.5, 'circle-pitch-alignment': 'map' }
    }, labelLayerId);
    this.mapInstance.addLayer({
      id: 'planned-route-traveled',
      type: 'line',
      source: 'planned-route-traveled',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': TRAVELED_ROUTE_COLOR, 'line-width': 9 }
    }, labelLayerId);
    // Butt cap: a round cap would stick out past the boundary, ahead of the driver
    this.mapInstance.addLayer({
      id: 'planned-route-traveled-tip',
      type: 'line',
      source: 'planned-route-traveled-tip',
      layout: { 'line-join': 'round', 'line-cap': 'butt' },
      paint: { 'line-color': TRAVELED_ROUTE_COLOR, 'line-width': 9 }
    }, labelLayerId);
  }

  private drawRouteChargingStations(routeData: Route, skin: MapSkin): void {
    if (!this.mapInstance) return;

    const { SingleChargingStations, MultiChargingStations } = this.mapService.getSingleAndMultipleChargingStations(routeData.RoutePlan);

    // Single-stop charging stations
    SingleChargingStations.forEach((charger: RoutePlanElement) => {
      const el = this.buildChargingStationElement(charger, skin, false);

      el.addEventListener('click', () => {
        this.mapService.ObservableActivePlanningPanelRouteElement.next({ type: 'charger', index: charger.ChargerIndex ?? undefined });
        this.openRouteChargerPopup(charger);
      });

      const marker = new Marker({ element: el, anchor: 'bottom' })
        .setLngLat([charger.Location.lng, charger.Location.lat])
        .addTo(this.mapInstance!);
      this.routeMarkers.push(marker);

      if (charger.BatteryReservedWarning) {
        this.addBatteryReservedWarningMarker(charger.Location);
      }
    });

    // Multiple-stop charging stations
    MultiChargingStations.forEach((multipleCharger: RoutePlanElement[]) => {
      const el = this.buildMultiChargingStationElement(multipleCharger, skin);

      el.addEventListener('click', () => {
        this.mapService.ObservableActivePlanningPanelRouteElement.next({ type: 'charger', index: multipleCharger[0].ChargerIndex ?? undefined });
        this.openRouteChargerPopup(multipleCharger[0]);
      });

      const marker = new Marker({ element: el, anchor: 'bottom' })
        .setLngLat([multipleCharger[0].Location.lng, multipleCharger[0].Location.lat])
        .addTo(this.mapInstance!);
      this.routeMarkers.push(marker);

      if (multipleCharger[0].BatteryReservedWarning) {
        this.addBatteryReservedWarningMarker(multipleCharger[0].Location);
      }
    });
  }

  /** Destination that is also a charging stop: the charger pin with its white disc checkered. */
  private drawChargerEndPoint(charger: RoutePlanElement, skin: MapSkin): void {
    if (!this.mapInstance) return;

    const el = this.buildChargingStationElement(charger, skin, false);

    // Same viewBox and box as the charging_station_*.svg pin img (sized in map.scss), so
    // the circle lands exactly on the pin's white disc (cx 60, cy 25.9, r 15.1) and covers
    // the bolt and stop number.
    const checker = document.createElement('div');
    checker.style.cssText = 'position: absolute; left: 0; top: 0; width: calc(48px * var(--map-marker-scale)); height: calc(48px * var(--map-marker-scale)); line-height: 0; pointer-events: none;';
    checker.innerHTML =
      `<svg viewBox='35.5 1.5 49 59.5' width='100%' height='100%' xmlns='http://www.w3.org/2000/svg'>` +
        `<defs>` +
          `<pattern id='charger-end-point-checker' x='60' y='25.9' width='10' height='10' patternUnits='userSpaceOnUse'>` +
            `<rect width='10' height='10' fill='#fff'/>` +
            `<path d='M0 0h5v5H0zM5 5h5v5H5z' fill='#0F1D2A'/>` +
          `</pattern>` +
        `</defs>` +
        `<circle cx='60' cy='25.9' r='15.1' fill='#fff'/>` +
        `<circle cx='60' cy='25.9' r='14.1' fill='url(#charger-end-point-checker)'/>` +
      `</svg>`;
    el.querySelector('.charge-index')?.remove();
    // Directly above the pin img, so the favorite star still draws on top
    el.querySelector('.charge-icon img')?.after(checker);

    el.addEventListener('click', () => {
      this.mapService.ObservableActivePlanningPanelRouteElement.next({ type: 'charger', index: charger.ChargerIndex ?? undefined });
      this.openRouteChargerPopup(charger);
    });

    const marker = new Marker({ element: el, anchor: 'bottom' })
      .setLngLat([charger.Location.lng, charger.Location.lat])
      .addTo(this.mapInstance);
    this.routeMarkers.push(marker);

    if (charger.BatteryReservedWarning) {
      this.addBatteryReservedWarningMarker(charger.Location);
    }
  }

  private buildChargingStationElement(charger: RoutePlanElement, skin: MapSkin, isMulti: boolean): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'map-charger';
    wrapper.style.cursor = 'pointer';

    // Charge time box
    let chargeTimeMin: any = charger.ChargeTimeMin;
    const chargeTimeHour = charger.ChargeTimeHour ?? 0;
    if (chargeTimeMin.toString().length < 2) {
      chargeTimeMin = '0' + chargeTimeMin.toString();
    }

    let chargeTimeHtml = '';
    if (chargeTimeHour > 0) {
      if (chargeTimeMin == '00') {
        chargeTimeHtml = `<div class='charge-time'><div class='charge-time-inner'> <h4>${chargeTimeHour}</h4> <small>${this.languageService.languageJSON.Unit_Hour_Short}</small></div></div>`;
      } else {
        chargeTimeHtml = `<div class='charge-time'><div class='charge-time-inner'> <h4>${chargeTimeHour}</h4> <small>${this.languageService.languageJSON.Unit_Hour_Short}</small> <h4>${chargeTimeMin}</h4><small>${this.languageService.languageJSON.Unit_Minute_Short}</small></div></div>`;
      }
    } else {
      chargeTimeHtml = `<div class='charge-time'><div class='charge-time-inner'> <h4>${chargeTimeMin}</h4><small>${this.languageService.languageJSON.Unit_Minute_Short}</small></div></div>`;
    }

    const iconSvg = `charging_station_${chargerPowerClass(charger.ChargePower ?? 0)}`;

    const boltColors: Record<string, Record<string, string>> = {
      light: { charging_station_1: '#6f6d0a', charging_station_2: '#2b6c3e', charging_station_3: '#662260' },
      dark: { charging_station_1: '#b5b451', charging_station_2: '#61aa78', charging_station_3: '#ad63a8' }
    };
    const numColor = boltColors[skin]?.[iconSvg] || '#1E293B';

    let html = chargeTimeHtml;
    html += `<div class="charge-icon" style="position: relative;">`;
    html += `<img src="assets/icons/${skin}/map/${iconSvg}.svg?v=4"/>`;
    // These inline styles override the .charge-index / .favorite-icon rules in
    // map.scss, so they have to apply the marker scale themselves to stay
    // aligned with the pin image that CSS is scaling.
    html += `<div class="charge-index" style="color: ${numColor}; transform: none; left: ${scaleRaster(22)}px; top: ${scaleRaster(14)}px; width: ${scaleRaster(12)}px; text-align: center; font-size: ${scaleRaster(14)}px; line-height: ${scaleRaster(13)}px; font-weight: 600;">${(charger.ChargerIndex ?? 0) + 1}</div>`;

    if (this.settingsService.getContainsFavoriteChargingStationId(charger.ChargingStationId ?? '')) {
      const fav = scaleRaster(15);
      html += `<div class="favorite-icon" style="position: absolute; top: 0px; right: ${scaleRaster(-2)}px; left: auto; margin: 0; width: ${fav}px; height: ${fav}px;"><img src="assets/icons/default/map/favorite_icon_planning.svg" style="width: ${fav}px; height: ${fav}px;"/></div>`;
    }

    html += `</div>`;

    wrapper.innerHTML = html;
    return wrapper;
  }

  private buildMultiChargingStationElement(chargers: RoutePlanElement[], skin: MapSkin): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'map-charger multiple-stop';
    wrapper.style.cursor = 'pointer';

    let chargeTimeHtml = `<div class='charge-time'>`;
    chargers.forEach((charger: RoutePlanElement) => {
      let chargeTimeMin: any = charger.ChargeTimeMin;
      const chargeTimeHour = charger.ChargeTimeHour ?? 0;
      if (chargeTimeMin.toString().length < 2) {
        chargeTimeMin = '0' + chargeTimeMin.toString();
      }

      chargeTimeHtml += `<div class='charge-time-inner'><span class='multi-charge-index'>
        <img class='charge-index-icon' src='assets/icons/default/map/chargingstation_icon.svg'/>${(charger.ChargerIndex ?? 0) + 1}</span>`;

      if (chargeTimeHour > 0) {
        if (chargeTimeMin == '00') {
          chargeTimeHtml += `<h4>${chargeTimeHour}</h4> <small>${this.languageService.languageJSON.Unit_Hour_Short}</small></div>`;
        } else {
          chargeTimeHtml += `<h4>${chargeTimeHour}</h4> <small>${this.languageService.languageJSON.Unit_Hour_Short}</small> <h4>${chargeTimeMin}</h4><small>${this.languageService.languageJSON.Unit_Minute_Short}</small></div>`;
        }
      } else {
        chargeTimeHtml += `<h4>${chargeTimeMin}</h4><small>${this.languageService.languageJSON.Unit_Minute_Short}</small></div>`;
      }
    });
    chargeTimeHtml += '</div>';

    const iconSvg = `charging_station_${chargerPowerClass(chargers[0].ChargePower ?? 0)}`;

    let html = chargeTimeHtml;
    html += `<div class='charge-icon' style='position: relative;'>`;
    html += `<img src='assets/icons/${skin}/map/${iconSvg}.svg?v=4'/>`;
    const boltColors: Record<string, Record<string, string>> = {
      light: { charging_station_1: '#6f6d0a', charging_station_2: '#2b6c3e', charging_station_3: '#662260' },
      dark: { charging_station_1: '#b5b451', charging_station_2: '#61aa78', charging_station_3: '#ad63a8' }
    };
    const boltColor = boltColors[skin]?.[iconSvg] || '#1E293B';

    const boltPath = 'M0,5.2c0.1,0.2,0.2,0.3,0.3,0.3l1.7,0.1L1.3,8.4c-0.1,0.2,0,0.4,0.2,0.5C1.6,9.1,1.9,9,2,8.9l3.9-4.6C6,4.1,6,3.9,6,3.7C5.9,3.6,5.7,3.5,5.6,3.5L3.9,3.4l0.8-2.8c0.1-0.2,0-0.4-0.2-0.5C4.5,0,4.5,0,4.3,0C4.2,0,4.1,0.1,4,0.1L0.1,4.7C0,4.9,0,5.1,0,5.2z';
    // Overlaid on the .charge-icon img, which CSS scales by --map-marker-scale;
    // position and size must track it or the bolts slide off the pin.
    html += `<div style='position: absolute; left: ${scaleRaster(12)}px; top: ${scaleRaster(8)}px; width: ${scaleRaster(24)}px; height: ${scaleRaster(24)}px; pointer-events: none;'>` +
      `<svg viewBox='0 0 24 24' width='${scaleRaster(24)}' height='${scaleRaster(24)}' xmlns='http://www.w3.org/2000/svg'>` +
        `<path d='${boltPath}' transform='translate(3.4 6.6) scale(1.2)' fill='${boltColor}'/>` +
        `<path d='${boltPath}' transform='translate(13.4 6.6) scale(1.2)' fill='${boltColor}'/>` +
      `</svg>` +
    `</div>`;
    html += `</div>`;

    wrapper.innerHTML = html;
    return wrapper;
  }

  private addBatteryReservedWarningMarker(location: LatLng): void {
    if (!this.mapInstance) return;

    const el = document.createElement('div');
    el.className = 'battery-reserved-warning';
    const badge = scaleRaster(20);
    el.innerHTML =
      `<svg viewBox='0 0 20 20' width='${badge}' height='${badge}' xmlns='http://www.w3.org/2000/svg'>` +
        `<circle cx='10' cy='10' r='10' fill='var(--warning-color-vibrant, #fd3250)'/>` +
        `<text x='10' y='10' text-anchor='middle' dominant-baseline='central' ` +
          `fill='#fff' font-weight='bold' font-size='13' font-family='sans-serif'>!</text>` +
      `</svg>`;

    // Offset is measured against the waypoint pin, which grows by the same
    // factor on touch devices — scale it too or the badge drifts off the pin.
    const marker = new Marker({ element: el, anchor: 'bottom', offset: [scaleRaster(15), scaleRaster(-35)] })
      .setLngLat([location.lng, location.lat])
      .addTo(this.mapInstance);
    this.routeMarkers.push(marker);
  }

  private openRouteChargerPopup(charger: RoutePlanElement): void {
    if (!this.mapInstance) return;
    const map = this.mapInstance;
    const point = map.project([charger.Location.lng, charger.Location.lat]);
    const mapWidth = map.getCanvas().width / (window.devicePixelRatio || 1);
    const popupAlign = mapWidth / 2 > point.x ? PopupAlign.Right : PopupAlign.Left;
    const widthAnchor = mapWidth / 2 > point.x ? -20 : 20;
    this.mapChargingPopupService.loadChargingStationPopup({
      align: popupAlign, widthAnchor, heightAnchor: 0, chargingStationId: charger.ChargingStationId ?? '',
      statusSelector: true, planBtn: false, searched: false,
      fallbackCoords: { lat: charger.Location.lat, lon: charger.Location.lng }
    });
  }

  private async drawRoadSurfaceAlerts(alerts: RoadSurfaceAlert[]): Promise<void> {
    if (!this.mapInstance || alerts.length === 0) return;

    const size = scaleRaster(48); // 24px CSS × 2 for retina
    const features: GeoJSON.Feature[] = [];
    const imageLoads: Promise<void>[] = [];
    const loadedIds = new Set<string>();

    for (const alert of alerts) {
      let iconName: string;
      let bgName: string;

      switch (alert.Type) {
        case 'roughness':    iconName = 'roughness'; break;
        case 'hydroplaning': iconName = 'hydroplaning'; break;
        case 'pothole':      iconName = 'road_damage'; break;
        case 'speedbump':    iconName = 'road_damage'; break;
        case 'slippery':     iconName = 'slipperiness'; break;
        default:             iconName = 'roughness'; break;
      }

      if (alert.Severity >= 0.75)     bgName = 'icon_background_critical';
      else if (alert.Severity >= 0.5) bgName = 'icon_background_major';
      else                            bgName = 'icon_background_minor';

      const id = `rsa-${iconName}-${bgName}`;
      const base = ROAD_SURFACE_ALERT_ICON_BASE;

      if (!loadedIds.has(id)) {
        loadedIds.add(id);
        imageLoads.push(this.ensureCompositeMapImage(id, `${base}/${bgName}.svg`, `${base}/${iconName}.svg`, size));
      }

      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [alert.Location.lng, alert.Location.lat] },
        properties: { icon: id }
      });
    }

    await Promise.all(imageLoads);

    if (!this.mapInstance) return;

    if (this.mapInstance.getLayer('road-surface-alerts-layer')) {
      this.mapInstance.removeLayer('road-surface-alerts-layer');
    }
    if (this.mapInstance.getSource('road-surface-alerts')) {
      this.mapInstance.removeSource('road-surface-alerts');
    }

    this.mapInstance.addSource('road-surface-alerts', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features }
    });

    this.mapInstance.addLayer({
      id: 'road-surface-alerts-layer',
      type: 'symbol',
      source: 'road-surface-alerts',
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-pitch-alignment': 'viewport',
        'icon-rotation-alignment': 'viewport'
      }
    });
  }

  private async drawIncidents(incidents: Incident[]): Promise<void> {
    if (!this.mapInstance || incidents.length === 0) return;

    const size = scaleRaster(48);
    const features: GeoJSON.Feature[] = [];
    const imageLoads: Promise<void>[] = [];
    const loadedIds = new Set<string>();

    for (const incident of incidents) {
      let iconName: string;
      let bgName: string;

      switch (incident.Type) {
        case 'accident':        iconName = 'accident'; break;
        case 'construction':    iconName = 'construction'; break;
        case 'congestion':      iconName = 'congestion'; break;
        case 'disabledVehicle': iconName = 'disabled_vehicle'; break;
        case 'massTransit':     iconName = 'mass_transit'; break;
        case 'plannedEvent':    iconName = 'planned_event'; break;
        case 'roadHazard':      iconName = 'road_hazard'; break;
        case 'roadClosure':     iconName = 'road_closure'; break;
        case 'weather':         iconName = 'weather_disruption'; break;
        case 'laneRestriction': iconName = 'lane_restriction'; break;
        case 'other':           iconName = 'other_incident'; break;
        default:                iconName = 'unknown'; break;
      }

      switch (incident.Severity) {
        case 'critical': bgName = 'incidents_icon_background_critical'; break;
        case 'major':    bgName = 'incidents_icon_background_major'; break;
        default:         bgName = 'incidents_icon_background_minor'; break;
      }

      const id = `inc-${iconName}-${bgName}`;
      const base = INCIDENT_ICON_BASE;

      if (!loadedIds.has(id)) {
        loadedIds.add(id);
        imageLoads.push(this.ensureCompositeMapImage(id, `${base}/${bgName}.svg`, `${base}/${iconName}.svg`, size));
      }

      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [incident.Location.lng, incident.Location.lat] },
        properties: { icon: id }
      });
    }

    await Promise.all(imageLoads);

    if (!this.mapInstance) return;

    if (this.mapInstance.getLayer('incidents-layer')) {
      this.mapInstance.removeLayer('incidents-layer');
    }
    if (this.mapInstance.getSource('incidents')) {
      this.mapInstance.removeSource('incidents');
    }

    this.mapInstance.addSource('incidents', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features }
    });

    this.mapInstance.addLayer({
      id: 'incidents-layer',
      type: 'symbol',
      source: 'incidents',
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-pitch-alignment': 'viewport',
        'icon-rotation-alignment': 'viewport'
      }
    });
  }

  // Load an HTMLImageElement from a URL
  private loadImageElement(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load: ${url}`));
      img.src = url;
    });
  }

  // Compose two SVG images (background + foreground) into a single map image
  private async ensureCompositeMapImage(id: string, bgUrl: string, fgUrl: string, size: number): Promise<void> {
    if (this.mapInstance?.hasImage(id)) return;
    try {
      const [bgImg, fgImg] = await Promise.all([
        this.loadImageElement(bgUrl),
        this.loadImageElement(fgUrl),
      ]);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(bgImg, 0, 0, size, size);
      ctx.drawImage(fgImg, 0, 0, size, size);
      const imageData = ctx.getImageData(0, 0, size, size);
      if (!this.mapInstance?.hasImage(id)) {
        this.mapInstance?.addImage(id, { width: size, height: size, data: new Uint8Array(imageData.data.buffer) }, { pixelRatio: 2 });
      }
    } catch {
      // Silently skip if SVGs fail to load
    }
  }

  private drawEVSpeedLimits(routeData: Route, skin: MapSkin): void {
    if (!this.mapInstance) return;

    routeData.RouteEVSpeedLimitSegments.forEach((segment: RouteEVSpeedLimitSegment) => {
      if (segment.SpeedLimit < 130 && segment.SpeedLimit > 0) {
        const speedValue = this.settingsService.getUnit().Speed == Unit.Metric
          ? segment.SpeedLimit
          : Math.round(this.utilsService.kmphToMph(segment.SpeedLimit));

        const el = document.createElement('div');
        el.className = 'speedlimit-outer';
        el.innerHTML = `<div class='speedlimit'>
          <img class='speedlimit-icon' src='assets/icons/light/ui/slider_small_button_battery.svg'/>
          <p class='speedlimit-value'>${speedValue}</p></div>`;
        el.title = this.languageService.languageJSON.Map_Tooltip_MaxSpeed?.toString() || '';

        const location = routeData.RoutePoints[segment.ShowInfoAtRouteIndex].Location;
        const marker = new Marker({ element: el, anchor: 'center' })
          .setLngLat([location.lng, location.lat])
          .addTo(this.mapInstance!);
        this.evSpeedLimitMarkers.push(marker);
      }
    });
  }

  public redrawEVSpeedLimits(routeData: Route): void {
    if (!this.mapInstance) return;
    this.clearEVSpeedLimitMarkers();
    const skin = this.settingsService.getMap().Skin;
    this.drawEVSpeedLimits(routeData, skin);
  }

  public redrawChargeTimes(routeData: Route): void {
    if (!this.mapInstance || !routeData?.RoutePlan) return;
    const skin = this.settingsService.getMap().Skin;
    const { SingleChargingStations, MultiChargingStations } =
      this.mapService.getSingleAndMultipleChargingStations(routeData.RoutePlan);

    this.routeMarkers.forEach((marker) => {
      const el = marker.getElement();
      if (!el.classList.contains('map-charger')) return;

      const { lng, lat } = marker.getLngLat();
      const multi = MultiChargingStations.find(
        (mc: RoutePlanElement[]) => mc[0].Location.lng === lng && mc[0].Location.lat === lat,
      );
      if (multi) {
        el.innerHTML = this.buildMultiChargingStationElement(multi, skin).innerHTML;
        return;
      }
      const single = SingleChargingStations.find(
        (c: RoutePlanElement) => c.Location.lng === lng && c.Location.lat === lat,
      );
      if (single) {
        el.innerHTML = this.buildChargingStationElement(single, skin, false).innerHTML;
      }
    });
  }

  private clearEVSpeedLimitMarkers(): void {
    this.evSpeedLimitMarkers.forEach(m => m.remove());
    this.evSpeedLimitMarkers = [];
  }

  private clearRouteMarkers(): void {
    this.routeMarkers.forEach(m => m.remove());
    this.routeMarkers = [];
    this.waypointMarkerElements = [];
    this.clearEVSpeedLimitMarkers();
  }

  /**
   * Gray out the waypoint markers the user has already passed during
   * navigation. `inactiveIdx` is NavigationService.NextWaypointIdx: waypoints
   * 1..inactiveIdx are behind the user. Pass null to clear the state
   * (navigation restart/exit).
   */
  public setInactiveWaypoints(inactiveIdx: number | null): void {
    this.inactiveWaypointCount = inactiveIdx ?? 0;
    this.applyInactiveWaypoints();
  }

  private applyInactiveWaypoints(): void {
    this.waypointMarkerElements.forEach((el, i) => {
      el.classList.toggle('grayscale', i < this.inactiveWaypointCount);
    });
  }

  public clearRoute(): void {
    // Remove route markers (HTML markers from drawRouteToMap – includes start/end markers)
    this.clearRouteMarkers();

    // Clear route charging station filter so tile CS layer shows them again
    this.mapChargingStationDrawerService.setRouteChargingStationIds([]);

    // Remove planned-route layers/sources (from drawRouteToMap)
    const plannedRouteLayers = ['planned-route-traveled-tip', 'planned-route-traveled', 'planned-route-traveled-start', 'planned-route-colored', 'planned-route-white', 'planned-route-outline'];
    const plannedRouteSources = ['planned-route', 'planned-route-segments', 'planned-route-traveled', 'planned-route-traveled-tip'];
    plannedRouteLayers.forEach(id => { if (this.mapInstance?.getLayer(id)) this.mapInstance!.removeLayer(id); });
    plannedRouteSources.forEach(id => { if (this.mapInstance?.getSource(id)) this.mapInstance!.removeSource(id); });

    // Remove existing layers and sources (from drawMainRoute / drawRoutePlan)
    if (this.mapInstance?.getLayer('route-arrows')) {
      this.mapInstance.removeLayer('route-arrows');
    }
    if (this.mapInstance?.getLayer('route-line-top')) {
      this.mapInstance.removeLayer('route-line-top');
    }
    if (this.mapInstance?.getLayer('route-line-middle')) {
      this.mapInstance.removeLayer('route-line-middle');
    }
    if (this.mapInstance?.getLayer('route-line-base')) {
      this.mapInstance.removeLayer('route-line-base');
    }
    if (this.mapInstance?.getSource('route')) {
      this.mapInstance.removeSource('route');
    }

    // Also remove alternative routes
    [0, 1, 2].forEach(index => {
      const routeId = `route-alt-${index}`;

      // Remove layers first
      if (this.mapInstance?.getLayer(`${routeId}-arrows`)) {
        this.mapInstance.removeLayer(`${routeId}-arrows`);
      }
      if (this.mapInstance?.getLayer(`${routeId}-line-top`)) {
        this.mapInstance.removeLayer(`${routeId}-line-top`);
      }
      if (this.mapInstance?.getLayer(`${routeId}-line-middle`)) {
        this.mapInstance.removeLayer(`${routeId}-line-middle`);
      }
      if (this.mapInstance?.getLayer(`${routeId}-line-base`)) {
        this.mapInstance.removeLayer(`${routeId}-line-base`);
      }
      // Remove source last
      if (this.mapInstance?.getSource(routeId)) {
        this.mapInstance.removeSource(routeId);
      }
    });

    // Remove route target marker
    if (this.mapInstance?.getLayer('route-target-marker')) {
      this.mapInstance.removeLayer('route-target-marker');
    }
    if (this.mapInstance?.getSource('route-target-marker')) {
      this.mapInstance.removeSource('route-target-marker');
    }

    // Remove battery warning
    if (this.mapInstance?.getLayer('battery-warning-marker')) {
      this.mapInstance.removeLayer('battery-warning-marker');
    }
    if (this.mapInstance?.getSource('battery-warning')) {
      this.mapInstance.removeSource('battery-warning');
    }

    // Remove battery empty
    if (this.mapInstance?.getLayer('battery-empty-marker')) {
      this.mapInstance.removeLayer('battery-empty-marker');
    }
    if (this.mapInstance?.getSource('battery-empty')) {
      this.mapInstance.removeSource('battery-empty');
    }

    // Remove road surface alerts (symbol layer)
    if (this.mapInstance?.getLayer('road-surface-alerts-layer')) {
      this.mapInstance.removeLayer('road-surface-alerts-layer');
    }
    if (this.mapInstance?.getSource('road-surface-alerts')) {
      this.mapInstance.removeSource('road-surface-alerts');
    }

    // Remove incidents (symbol layer)
    if (this.mapInstance?.getLayer('incidents-layer')) {
      this.mapInstance.removeLayer('incidents-layer');
    }
    if (this.mapInstance?.getSource('incidents')) {
      this.mapInstance.removeSource('incidents');
    }

    // Remove failed route elements (line, markers, end marker)
    this.clearFailedRoute();
  }

  public drawTripToMap(tripPoints: TripPoint[]): void {
    if (!this.mapInstance || tripPoints.length < 2) return;

    this.clearTrip();

    const skin = this.settingsService.getMap().Skin;
    const labelLayerId = this.getFirstLabelLayerId();

    const fullCoords: [number, number][] = tripPoints.map(p => [p.lon, p.lat]);

    const segmentColors = tripPoints.slice(1)
      .map(p => this.utilsService.rgbToHex(255 - p.soc * 255, p.soc * 255, 0));
    const segmentFeatures = this.buildColorRunFeatures(fullCoords, segmentColors);

    this.mapInstance.addSource('trip', {
      type: 'geojson',
      data: { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: fullCoords } }
    });

    this.mapInstance.addSource('trip-segments', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: segmentFeatures }
    });

    if (skin === MapSkin.Light) {
      this.mapInstance.addLayer({
        id: 'trip-outline',
        type: 'line',
        source: 'trip',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#ccc', 'line-width': 18 }
      }, labelLayerId);
    }

    this.mapInstance.addLayer({
      id: 'trip-white',
      type: 'line',
      source: 'trip',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': skin === MapSkin.Light ? '#ffffff' : '#000000', 'line-width': 16 }
    }, labelLayerId);

    this.mapInstance.addLayer({
      id: 'trip-colored',
      type: 'line',
      source: 'trip-segments',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': ['get', 'color'], 'line-width': 9 }
    }, labelLayerId);

    const startEl = document.createElement('div');
    startEl.style.lineHeight = '0';
    startEl.innerHTML = `<img src='assets/icons/${skin}/map/route_point_start.svg?v=2' width='${scaleRaster(48)}' height='${scaleRaster(48)}'/>`;
    this.tripMarkers.push(
      new Marker({ element: startEl, anchor: 'bottom' })
        .setLngLat([tripPoints[0].lon, tripPoints[0].lat])
        .addTo(this.mapInstance)
    );

    const endEl = document.createElement('div');
    endEl.style.lineHeight = '0';
    endEl.innerHTML = `<img src='assets/icons/${skin}/map/route_point_target.svg?v=2' width='${scaleRaster(48)}' height='${scaleRaster(48)}'/>`;
    this.tripMarkers.push(
      new Marker({ element: endEl, anchor: 'bottom' })
        .setLngLat([tripPoints.at(-1)!.lon, tripPoints.at(-1)!.lat])
        .addTo(this.mapInstance)
    );

    const bounds = new LngLatBounds();
    tripPoints.forEach(p => bounds.extend([p.lon, p.lat]));
    if (!bounds.isEmpty()) {
      this.mapInstance.fitBounds(bounds, { padding: 100, animate: true });
    }
  }

  public clearTrip(): void {
    this.tripMarkers.forEach(m => m.remove());
    this.tripMarkers = [];

    ['trip-colored', 'trip-white', 'trip-outline'].forEach(id => {
      if (this.mapInstance?.getLayer(id)) this.mapInstance!.removeLayer(id);
    });
    ['trip', 'trip-segments'].forEach(id => {
      if (this.mapInstance?.getSource(id)) this.mapInstance!.removeSource(id);
    });
  }

  public drawFailedRoute(waypoints: Waypoint[], skin: MapSkin, maxReachedWaypoint: number): void {
    if (!this.mapInstance || waypoints.length < 2) return;

    // End marker
    this.mapDrawerHelperService.addEndMarker(skin,
      new LatLng(waypoints.at(-1)!.lat, waypoints.at(-1)!.lng), true);

    // Failed route beelines between waypoints
    this.mapDrawerHelperService.addFailedRouteLine(waypoints, false);

    // Intermediate waypoint markers
    const waypointsCopy = [...waypoints];
    waypointsCopy.shift();
    waypointsCopy.pop();
    for (let i = 0; i < waypointsCopy.length; i++) {
      const routePointIcon = maxReachedWaypoint > i ? 'route_point_waypoint.svg?v=3' : 'route_point_waypoint_failed.svg?v=3';
      const el = document.createElement('div');
      el.className = 'waypoint-icon-outer';
      el.innerHTML = `<div class='waypoint-icon'><img src='assets/icons/${skin}/map/${routePointIcon}'></div><div class='waypoint-idx'>${i + 1}</div>`;
      const marker = new Marker({ element: el, anchor: 'bottom' })
        .setLngLat([waypointsCopy[i].lng, waypointsCopy[i].lat])
        .addTo(this.mapInstance);
      this.failedRouteMarkers.push(marker);
    }
  }

  private clearFailedRoute(): void {
    this.failedRouteMarkers.forEach(m => m.remove());
    this.failedRouteMarkers = [];
    this.mapDrawerHelperService.removeFailedRouteLine();
    this.mapDrawerHelperService.removeStartEndMarkers();
  }

  private keepChargingStationLayersOnTop(): void {
    this.keepLayerOnTop('cursor-layer');
    this.keepLayerOnTop('cs-groups-layer');
    this.keepLayerOnTop('cs-layer');
    this.keepLayerOnTop('cs-fav-layer');
  }

  keepLayerOnTop(layerId: string) {
    if (this.mapInstance?.getLayer(layerId)) {
      this.mapInstance?.moveLayer(layerId);
    }
  }
}
